import config from "../config";

export const AUTH_POPUP_MESSAGE_TYPE = "employees_google_auth_popup_result";

const normalizeNoTrailingSlash = (url) => (url || "").replace(/\/+$/, "");

const getFrontendBaseUrl = () => {
  const baseFromEnv = normalizeNoTrailingSlash(process.env.REACT_APP_BASEURL || "");
  if (baseFromEnv) return baseFromEnv;

  if (typeof window === "undefined") return "";

  const origin = window.location.origin;
  const mainDir = (process.env.REACT_APP_MAINDIR || "").replace(/^\/+|\/+$/g, "");
  return mainDir ? `${origin}/${mainDir}` : origin;
};

const buildCallbackUrl = ({ popup = false } = {}) => {
  const base = `${getFrontendBaseUrl()}/auth/callback`;
  if (!popup) return base;

  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}popup=1`;
};

const getBackendBaseUrl = () => normalizeNoTrailingSlash(config.baseApiUrl || "");

export const clearAuthSessionStorage = () => {};

const isSafeParentRedirectUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") return false;

  try {
    const parsed = new URL(rawUrl);
    if (parsed.protocol !== "https:") return false;

    const allowedHostsRaw = String(
      process.env.REACT_APP_PARENT_REDIRECT_HOSTS || "sites.google.com"
    )
      .split(",")
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);

    const host = String(parsed.hostname || "").toLowerCase();
    return allowedHostsRaw.some(
      (allowed) => host === allowed || host.endsWith(`.${allowed}`)
    );
  } catch (error) {
    return false;
  }
};

export const sanitizeParentRedirectUrl = (rawUrl) => {
  const value = String(rawUrl || "").trim();
  return isSafeParentRedirectUrl(value) ? value : "";
};

const getParentIframeReferrerUrl = () => {
  if (typeof window === "undefined") return "";
  if (window.self === window.top) return "";

  const ref = String(document.referrer || "").trim();
  const safeRef = isSafeParentRedirectUrl(ref) ? ref : "";
  console.log("[auth] iframe referrer", { referrer: ref || "(empty)", safeRef });
  return safeRef;
};

export const getGoogleRedirectEndpoint = ({ popup = false } = {}) => {
  const backendBaseUrl = getBackendBaseUrl();
  if (!backendBaseUrl) {
    return "";
  }

  const callbackUrl = buildCallbackUrl({ popup });
  const parentFromEnv = sanitizeParentRedirectUrl(
    process.env.REACT_APP_PARENT_REDIRECT_URL
  );
  const parentFromIframe = popup ? "" : getParentIframeReferrerUrl();
  const parentRedirectUrl = parentFromEnv || parentFromIframe;

  const base = `${backendBaseUrl}/api/auth/google/redirect?redirect_url=${encodeURIComponent(
    callbackUrl
  )}`;

  if (!parentRedirectUrl) {
    console.log("[auth] oauth endpoint", { popup, callbackUrl, mode: "no-parent" });
    return base;
  }

  console.log("[auth] oauth endpoint", {
    popup,
    callbackUrl,
    mode: "with-parent",
    parentRedirectUrl,
  });
  return `${base}&parent_redirect_url=${encodeURIComponent(parentRedirectUrl)}`;
};

const navigateToUrl = (url, { replace = false } = {}) => {
  if (typeof window === "undefined" || !url) {
    return { ok: false, mode: "noop" };
  }

  const isFramed = window.self !== window.top;
  if (isFramed) {
    try {
      if (replace) {
        window.top.location.replace(url);
      } else {
        window.top.location.href = url;
      }
      return { ok: true, mode: "top" };
    } catch (error) {
      // Ignore and fallback below.
    }

    try {
      const opened = window.open(url, "_blank", "noopener,noreferrer");
      if (opened) {
        return { ok: true, mode: "new_tab" };
      }
    } catch (error) {
      // Ignore and fallback below.
    }
  }

  if (replace) {
    window.location.replace(url);
    return { ok: true, mode: "self_replace" };
  }

  window.location.assign(url);
  return { ok: true, mode: "self_assign" };
};

const openCenteredPopup = (url) => {
  if (typeof window === "undefined" || !url) {
    return { ok: false, mode: "noop" };
  }

  const width = 520;
  const height = 700;
  const left = Math.max(0, Math.floor((window.screen.width - width) / 2));
  const top = Math.max(0, Math.floor((window.screen.height - height) / 2));
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    "resizable=yes",
    "scrollbars=yes",
  ].join(",");

  const popup = window.open(url, "employees_google_login_popup", features);
  if (!popup) {
    return { ok: false, mode: "popup_blocked" };
  }

  try {
    popup.focus();
  } catch (error) {
    // No-op.
  }

  return { ok: true, mode: "popup" };
};

export const redirectToGoogleLogin = () => {
  if (typeof window === "undefined") {
    return;
  }

  const popupEndpoint = getGoogleRedirectEndpoint({ popup: true });
  if (!popupEndpoint) {
    return;
  }

  return openCenteredPopup(popupEndpoint);
};

export const isPopupCallbackRequest = (search = "") => {
  try {
    const params = new URLSearchParams(search || "");
    return params.get("popup") === "1";
  } catch (error) {
    return false;
  }
};

const getFrontendOrigin = () => {
  try {
    return new URL(getFrontendBaseUrl()).origin;
  } catch (error) {
    return typeof window !== "undefined" ? window.location.origin : "";
  }
};

export const notifyPopupAuthResultAndClose = ({ success, reason = "" }) => {
  if (typeof window === "undefined") return;
  if (!window.opener || window.opener.closed) return;

  const payload = {
    type: AUTH_POPUP_MESSAGE_TYPE,
    success: Boolean(success),
    reason: String(reason || ""),
  };

  try {
    window.opener.postMessage(payload, getFrontendOrigin() || "*");
  } catch (error) {
    window.opener.postMessage(payload, "*");
  }

  window.close();
};

export const handleUnauthorizedAndRelogin = () => {
  clearAuthSessionStorage();
  redirectToGoogleLogin();
};

export const replaceLocation = (url) => {
  return navigateToUrl(url, { replace: true });
};
